import { create } from 'zustand'

/**
 * UI-Präferenzen: Schriftgröße, Modus, Performance, Thema.
 *
 * Bewusst NICHT im Server-Store: Diese Einstellungen müssen beim allerersten Paint
 * stehen, bevor irgendein Request beantwortet ist — sonst springt die Oberfläche
 * für genau die Nutzer, die auf große Schrift angewiesen sind. Sie werden später
 * zusätzlich am Konto gespiegelt, damit ein neues Gerät sie übernimmt.
 */

export type Scale = 'normal' | 'large' | 'xlarge'
export type Mode = 'standard' | 'einfach'
export type Perf = 'auto' | 'low'
export type Theme = 'system' | 'light' | 'dark'
/** Deutsch oder Englisch. Mehr wird es absehbar nicht — siehe `i18n/`. */
export type Sprache = 'de' | 'en'

export interface Prefs {
  scale: Scale
  mode: Mode
  perf: Perf
  theme: Theme
  sprache: Sprache
  /**
   * Darf der eigene Server übersetzen, wenn der Browser es nicht kann?
   *
   * Betrifft NUR Geräte ohne eingebaute Übersetzung — auf dem iPhone also
   * immer, in Chrome nie. Steht auf , weil die App sonst eine Zusage
   * bricht, die sie in den Einstellungen gibt: Wer auf Englisch gestellt ist,
   * bekommt eine deutsche Nachricht auf Englisch.
   *
   * Wer das abstellt, bekommt auf dem iPhone wieder einsprachige Nachrichten —
   * und der Server sieht dafür nie einen Chat-Klartext. Der Handel steht in
   *  und in SECURITY.md §3.
   */
  serverUebersetzt: boolean
}

/**
 * Die Sprache des Browsers als Startwert.
 *
 * Nicht als Festlegung: Wer die App auf Englisch will, obwohl das Telefon auf
 * Deutsch steht, stellt es um. Aber beim allerersten Öffnen ist die Sprache
 * des Geräts die beste Vermutung, die es gibt — und die einzige, die man
 * treffen kann, bevor jemand etwas eingestellt hat.
 */
export function spracheVomGeraet(): Sprache {
  return navigator.language?.toLowerCase().startsWith('de') ? 'de' : 'en'
}

const DEFAULTS: Prefs = {
  scale: 'normal', mode: 'standard', perf: 'auto', theme: 'system', sprache: 'de',
  serverUebersetzt: true,
}
const STORAGE_KEY = 'fh.prefs'

/**
 * Erkennt schwache Geräte und schaltet den Performance-Modus vor. Nutzer können
 * jederzeit überstimmen — die Heuristik entscheidet nur den Startwert (Anf. 67).
 */
export function detectLowEnd(): boolean {
  const nav = navigator as Navigator & {
    deviceMemory?: number
    connection?: { saveData?: boolean; effectiveType?: string }
  }
  if (nav.connection?.saveData) return true
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2) return true
  if (nav.hardwareConcurrency && nav.hardwareConcurrency <= 2) return true
  const type = nav.connection?.effectiveType
  if (type === 'slow-2g' || type === '2g' || type === '3g') return true
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function loadPrefs(): Prefs {
  let stored: Partial<Prefs> = {}
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<Prefs>
  } catch {
    // Privater Modus, blockierte Site-Daten, kaputter Eintrag — Defaults sind richtig.
  }
  const prefs = { ...DEFAULTS, ...stored }
  if (!stored.perf && detectLowEnd()) prefs.perf = 'low'
  if (!stored.sprache) prefs.sprache = spracheVomGeraet()
  return prefs
}

/** Schreibt die Präferenzen als Attribute an <html>, wo das CSS sie liest. */
export function applyPrefs(p: Prefs): void {
  const root = document.documentElement
  root.dataset.scale = p.scale
  root.dataset.mode = p.mode
  root.dataset.perf = p.perf
  if (p.theme === 'system') delete root.dataset.theme
  else root.dataset.theme = p.theme
  /*
   * `lang` ist nicht Kosmetik: Vorlesefunktionen entscheiden daran, mit
   * welcher Aussprache sie lesen. Ein deutscher Screenreader, der englische
   * Sätze deutsch ausspricht, ist unverständlicher als gar keiner.
   */
  root.lang = p.sprache
}

interface PrefsStore extends Prefs {
  set<K extends keyof Prefs>(key: K, value: Prefs[K]): void
}

export const usePrefs = create<PrefsStore>((set, get) => ({
  ...loadPrefs(),
  set(key, value) {
    set({ [key]: value } as Pick<Prefs, typeof key>)
    const { scale, mode, perf, theme, sprache, serverUebersetzt } = get()
    const next = { scale, mode, perf, theme, sprache, serverUebersetzt, [key]: value } as Prefs
    applyPrefs(next)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // Speichern kann fehlschlagen; die Einstellung gilt trotzdem für diese Sitzung.
    }
  },
}))
